import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import useAuthStore, { AuthState } from '../stores/useAuthStore';

function InstallmentChangePage() {
  const [minInstallmentLimit, setMinInstallmentLimit] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openConfirm, setOpenConfirm] = useState(false);

  // Separate subscriptions to avoid returning a new object each time.
  const customerId = useAuthStore((state: AuthState) => state.customerId);
  const adminInfo = useAuthStore((state: AuthState) => state.adminInfo);
  const user = useAuthStore((state: AuthState) => state.user);

  // Determine the current installment limit from adminInfo or user.
  const currentLimit =
    adminInfo?.minimumLimitForInstallment ?? user?.minimumLimitForInstallment;

  const handleSave = async () => {
    // Reset messages
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append('token', 'RasyoIoToken2021');
      // API expects customer id under the "Id" parameter.
      formData.append('Id', customerId);
      formData.append('MinimumInstallmentLimit', minInstallmentLimit);

      const response = await fetch('https://pay.duyu.alter.net.tr/api/Installment', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('İstek başarısız oldu.');
      }

      // Parse the JSON response; expected format: { "result": true }
      const data = await response.json();

      if (data.result === true) {
        setSuccess('Taksit limiti başarıyla güncellendi.');
        const newLimit = Number(minInstallmentLimit);

        // If admin, update the store only when the value is different.
        if (adminInfo) {
          if (adminInfo.minimumLimitForInstallment !== newLimit) {
            useAuthStore.setState({
              adminInfo: { ...adminInfo, minimumLimitForInstallment: newLimit },
            });
          }
        } else if (user) {
          if (user.minimumLimitForInstallment !== newLimit) {
            useAuthStore.setState({
              user: { ...user, minimumLimitForInstallment: newLimit },
            });
          }
        }
      } else {
        setError('Taksit limiti güncellenemedi.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bir şeyler yanlış gitti.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmOpen = () => {
    setOpenConfirm(true);
  };

  const handleConfirmClose = () => {
    setOpenConfirm(false);
  };

  const handleConfirmYes = async () => {
    setOpenConfirm(false);
    await handleSave();
  };

  return (
    // Outer Box for centering the component on the screen.
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '70vh',
        backgroundColor: 'background.default',
        p: 2,
      }}
    >
      {/* Inner container with a fixed max width and additional styling */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 800,
          p: 3,
          borderRadius: 1,
          boxShadow: 2,
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="h5" mb={2} align="center">
          Taksit Limiti Ayarları
        </Typography>
        {/* Display the current installment limit */}
        <Typography variant="body1" align="center" mb={2}>
          {`Güncel Limit: ${currentLimit !== undefined ? currentLimit : 'Belirlenmedi'}`}
        </Typography>

        <TextField
          label="Minimum Taksit Limiti"
          value={minInstallmentLimit}
          onChange={(e) => setMinInstallmentLimit(e.target.value)}
          type="number"
          fullWidth
        />

        <Box mt={2} display="flex" justifyContent="center">
          <Button variant="contained" onClick={handleConfirmOpen} disabled={isLoading}>
            {isLoading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </Box>

        {/* Confirmation Dialog */}
        <Dialog open={openConfirm} onClose={handleConfirmClose}>
          <DialogTitle>Emin misiniz?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {`Minimum Taksit Limiti "${minInstallmentLimit}" olarak ayarlamak istediğinize emin misiniz?`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmClose} color="primary">
              Hayır
            </Button>
            <Button onClick={handleConfirmYes} color="primary" autoFocus>
              Evet
            </Button>
          </DialogActions>
        </Dialog>

        {/* Display messages */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
      </Box>
    </Box>
  );
}

export default InstallmentChangePage;
